import type { WorkspaceResponseV2 } from '@emma/api_client/types';
import { maxSecurityScanNotificationUnsubscriptions } from '@emma/core/consts';
import { useToast } from '@lego/klik-ui';
import { type ReactNode, useCallback, useState } from 'react';
import { useSWRConfig } from 'swr';
import { z } from 'zod';
import { EditableDetailsBox } from '@/components/common/details/EditableDetailsBox';
import { buildWorkspaceDetailsSections } from '@/components/workspace/WorkspaceDetailsSections';
import { useCanWrite, useUpdateWorkspace } from '@/hooks/workspace';
import { ToastOptions } from '@/utility/ToastOptions';
import { ToastFactory } from '@/utility/toast_factory';

type Props = {
  workspace: WorkspaceResponseV2;
  isLoading?: boolean;
};

const VULNERABILITY_EMAIL_NOTIFICATIONS_FIELD = 'Vulnerability email notifications';

const FIELD_NAME_MAP: Record<string, keyof WorkspaceResponseV2> = {
  Description: 'description',
  'Product ID': 'enterpriseArchitectureProductId',
  [VULNERABILITY_EMAIL_NOTIFICATIONS_FIELD]: 'securityScanNotificationUnsubscriptions',
};

const uuidSchema = z.string().uuid();

const parseUnsubscriptionObjectIds = (raw: string): string[] =>
  Array.from(
    new Set(
      raw
        .split(/[\n,;]/)
        .map((entry) => entry.trim().toLowerCase())
        .filter(Boolean)
    )
  );

type ValidationFailure = { error: string };
type ValidationSuccess = { value: string | string[] | null };

const transformProductId = (raw: string): ValidationFailure | ValidationSuccess => {
  const productId = raw ? `PRO-${raw}` : raw;
  if (productId && !/^PRO-\d{3,10}$/.test(productId)) {
    return { error: 'Product ID must contain 3 to 10 digits' };
  }
  return { value: productId };
};

const transformUnsubscriptions = (raw: string): ValidationFailure | ValidationSuccess => {
  const parsed = parseUnsubscriptionObjectIds(raw);
  if (parsed.length > maxSecurityScanNotificationUnsubscriptions) {
    return {
      error: `Vulnerability notification exclusions must contain at most ${maxSecurityScanNotificationUnsubscriptions} entries`,
    };
  }

  const invalid = parsed.find((entry) => !uuidSchema.safeParse(entry).success);
  if (invalid) {
    return { error: `Not a valid object ID: ${invalid}` };
  }

  return { value: parsed.length === 0 ? [] : parsed };
};

export const WorkspaceDetails = ({ workspace, isLoading = false }: Props) => {
  const canWrite = useCanWrite(workspace);
  const workspaceDetailsSections = buildWorkspaceDetailsSections(workspace, isLoading);
  const [editedValue, setEditedValue] = useState('');
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const { mutate } = useSWRConfig();
  const toast = useToast();

  const updateWorkspace = useUpdateWorkspace(workspace.id);

  const cacheKey = `workspaces/${workspace.id}`;

  const handleStartEdit = useCallback(
    (key: string, currentValue: ReactNode) => {
      let initialValue = String(currentValue ?? '');
      if (key === 'Product ID' && initialValue.startsWith('PRO-')) {
        initialValue = initialValue.slice(4);
      }
      if (key === VULNERABILITY_EMAIL_NOTIFICATIONS_FIELD) {
        initialValue = workspace.securityScanNotificationUnsubscriptions?.join('\n') ?? '';
      }
      setEditedValue(initialValue);
      setEditingKey(key);
    },
    [workspace.securityScanNotificationUnsubscriptions]
  );

  const handleCancelEdit = useCallback(() => {
    setEditedValue('');
    setEditingKey(null);
  }, []);

  const handleSave = useCallback(
    async (key: string, value?: string) => {
      const rawValue = value ?? editedValue;
      const apiFieldName = FIELD_NAME_MAP[key];

      if (!apiFieldName) return;

      let result: ValidationFailure | ValidationSuccess = { value: rawValue };
      if (apiFieldName === 'enterpriseArchitectureProductId') {
        result = transformProductId(rawValue);
      } else if (apiFieldName === 'securityScanNotificationUnsubscriptions') {
        result = transformUnsubscriptions(rawValue);
      }

      if ('error' in result) {
        toast(ToastFactory.validationError(result.error));
        return;
      }

      setIsSaving(true);
      try {
        const updatedWorkspace = await updateWorkspace({ [apiFieldName]: result.value });
        await mutate(cacheKey, updatedWorkspace, { revalidate: false });
        setEditingKey(null);
        if (key === VULNERABILITY_EMAIL_NOTIFICATIONS_FIELD) {
          toast(
            ToastOptions.success('Saved', 'Vulnerability notification settings updated.', 4000)
          );
        }
      } catch {
        // Already toasted and logged by the mutation hook; caught only to
        // avoid an unhandled promise rejection here.
      } finally {
        setIsSaving(false);
      }
    },
    [editedValue, updateWorkspace, mutate, cacheKey, toast]
  );

  return (
    <EditableDetailsBox
      editableFields={
        canWrite ? ['Description', 'Product ID', VULNERABILITY_EMAIL_NOTIFICATIONS_FIELD] : []
      }
      editedValue={editedValue}
      editingKey={editingKey}
      isSaving={isSaving}
      onCancelEdit={handleCancelEdit}
      onSave={handleSave}
      onStartEdit={handleStartEdit}
      onValueChange={setEditedValue}
      sections={workspaceDetailsSections}
      title={workspace.name}
    />
  );
};
